(ns app.front.view.group.admin.inspections.visit-result
  (:require [app.domain.group.model :as group-model]
            [app.domain.inspection.model :as inspection-model]
            [app.front.entities :as entities]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.multi-select :as ms]
            [app.front.ui.select :as ui-select]
            [app.front.ui.table :as ui-table]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.inspections.db :as iadb]
            [app.front.view.group.admin.inspections.subs :as ias]
            [app.front.view.group.admin.subs :as gas]
            [app.i18n.model :as i18n-model]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::visit-result-in-use
  (fn [db [_ visit-result]]
    (contains? (::iadb/visit-results-in-use db) (:db/id visit-result))))

(rf/reg-event-db ::toggle-row-expansion
  (fn [db [_ id]]
    (let [expanded? (-> db ::expanded-rows (contains? id))]
      (update db ::expanded-rows (comp set (if expanded? disj conj)) id))))

(rf/reg-sub ::expanded-rows
  (fn [db]
    (::expanded-rows db #{})))

(rf/reg-sub ::visit-result-data
  :-> ::visit-result)

(rf/reg-sub ::get-visit-result-data
  :<- [::visit-result-data]
  (fn [data [_ key]]
    (get data key)))

(rf/reg-sub ::invalid-visit-result-data?
  :<- [::visit-result-data]
  (fn [{:inspection-visit-result/keys [names status-type]}]
    (or (i18n-model/blank? names)
        (nil? status-type))))

(rf/reg-sub ::no-edit-changes?
  (fn [[_ id]]
    [(rf/subscribe [::entities/entity id])
     (rf/subscribe [::visit-result-data])])
  (fn [[entity edited]]
    (= (-> (select-keys entity inspection-model/visit-result-editable-keys)
           (update :inspection-visit-result/names i18n-model/strings-map))
       (select-keys edited inspection-model/visit-result-editable-keys))))

(rf/reg-event-db ::set-visit-result-field
  (fn [db [_ key data]]
    (assoc-in db [::visit-result key] data)))

(rf/reg-event-db ::set-create-open
  (fn [db [_ value]]
    (assoc db ::create-open? value)))

(rf/reg-sub ::creating-new-visit-result?
  :-> ::create-open?)

(rf/reg-sub ::editing-visit-result?
  :<- [::visit-result-data]
  :-> some?)

(rf/reg-event-db ::set-visit-result
  (fn [db [_ {id :db/id :as visit-result}]]
    (cond-> (assoc db ::visit-result (some-> visit-result
                                             (select-keys (conj inspection-model/visit-result-editable-keys :db/id))
                                             (update :inspection-visit-result/names i18n-model/strings-map)))
      id (update ::expanded-rows conj id))))

(defn save-data [db]
  (-> db
      ::visit-result
      (select-keys [:inspection-visit-result/names
                    :inspection-visit-result/status-type
                    :inspection-visit-result/rules])
      (update :inspection-visit-result/rules (fnil identity #{}))))

(rf/reg-event-fx ::create-visit-result
  (fn [{:keys [db]} [_ authority-group-id]]
    {:comm/command {:command    :inspection/create-visit-result
                    :method     :post
                    :data       (util/mmap {:data (save-data db)}
                                           authority-group-id)
                    :on-success [::save-visit-result.success]
                    :notify     {:success :inspection-visit-result/created}}}))

(rf/reg-event-fx ::edit-visit-result
  (fn [{:keys [db]} _]
    {:comm/command {:command    :inspection/edit-visit-result
                    :method     :post
                    :data       {:data (save-data db)
                                 :id   (-> db ::visit-result :db/id)}
                    :on-success [::save-visit-result.success]
                    :notify     {:success :inspection-visit-result/saved}}}))

(rf/reg-event-fx ::delete-visit-result
  (fn [_ [_ {id :db/id}]]
    {:comm/command {:command    :inspection/delete-visit-result
                    :method     :post
                    :data       {:id id}
                    :on-success [::entities/apply-changes]
                    :notify     {:success :inspection-visit-result/deleted}}}))

(rf/reg-event-fx ::set-default-quick-pass-result
  (fn [_ [_ authority-group-id {id :db/id}]]
    {:comm/command {:command    :inspection/set-default-quick-pass-result
                    :method     :post
                    :data       (p/assoc-if {:authority-group-id authority-group-id}
                                            :visit-result-id id)
                    :on-success [::entities/apply-changes]
                    :notify     {:success :changes-saved}}}))

(rf/reg-event-fx ::save-visit-result.success
  (fn [_ [_ status resp]]
    {:fx [[:dispatch [::entities/apply-changes status resp]]
          [:dispatch [::set-create-open false]]
          [:dispatch [::set-visit-result nil]]]}))

(defn- name-field [tr show-label?]
  (let [strings (<sub [::get-visit-result-data :inspection-visit-result/names])]
    [ui-i18n/localized-string-edit
     {:id                  "visit-result-name"
      :type                :text
      :label-text          (when show-label? (tr [:inspection-visit-result/names]))
      :light               true
      :required            show-label?
      :strings             strings
      :on-change           #(rf/dispatch-sync [::set-visit-result-field :inspection-visit-result/names %])
      :allowed-locales-sub ::gas/allowed-locales}]))

(defn- name-col [visit-result]
  (let [lang (ui-i18n/get-actual-language-to-edit
               (-> visit-result :inspection-visit-result/names i18n-model/strings-map)
               (<sub [::ui-i18n/editor-language ::gas/allowed-locales]))]
    (inspection-model/visit-result-name visit-result lang)))

(defn- rules-selector [tr id disabled rules]
  (let [current-rules    (or rules (<sub [::get-visit-result-data :inspection-visit-result/rules]))
        close-selected?  (contains? current-rules :inspection.visit.result-rule/close-inspection)
        feature-enabled? (<sub [::ff/enabled? :feature-flag.id/outstanding-bill-close-inspection])
        available-rules  (cond->> inspection-model/visit-result-rules
                           (not (and feature-enabled? close-selected?))
                           (remove #{:inspection.visit.result-rule/outstanding-bill-payment-required})

                           (-> (<sub [::gas/group]) group-model/primary-permit-type (= :permit-type/PWO))
                           (remove #{:inspection.visit.result-rule/no-deficiencies-allowed}))]
    [ms/multi-checkbox
     {:id          (str "rules-selector-" id)
      :val->str    #(tr [%])
      :values      available-rules
      :on-change   #(>evt [::set-visit-result-field :inspection-visit-result/rules (set %)])
      :selected    current-rules
      :label-text  (tr [:inspection-visit-result/rules])
      :disabled    (when disabled available-rules)
      :tr          tr
      :all-toggle? false}]))

(def status-type-color {:inspection.visit.result-status-type/in-progress "bg-status-neutral-02"
                        :inspection.visit.result-status-type/passed      "bg-status-success"
                        :inspection.visit.result-status-type/failed      "bg-status-error"
                        :inspection.visit.result-status-type/completed   "bg-ui-black"})

(defn- status-type-selector [tr show-label?]
  (let [value (<sub [::get-visit-result-data :inspection-visit-result/status-type])]
    [:div.flex.flex-row.items-end.gap-x-2
     [:div.flex-col
      [ui-select/select {:values       inspection-model/visit-result-status-types
                         :allow-empty? false
                         :required     true
                         :val->str     #(tr [%])
                         :label-text   (when show-label? (tr [:inspection-visit-result/status-type]))
                         :id           "visit-result-status-type"
                         :on-change    #(>evt [::set-visit-result-field :inspection-visit-result/status-type %])
                         :value        value}]]

     [:div.flex-col.w-10.h-10 {:class (status-type-color value)}]]))

(defn- status-type-col [tr {:inspection-visit-result/keys [status-type]}]
  [:div.flex.flex-row.space-between.items-end.gap-x-2
   [:div.w-24 (tr [status-type])]
   [:div.flex-col.w-4.h-4 {:class (status-type-color status-type)}]])

(defn- visit-result-editor [{:keys [tr authority-group-id]}]
  [:div.flex.flex-col.p-4.gap-y-2.border.border-solid.border-blue-600.bg-gray-200
   [name-field tr true]
   [status-type-selector tr true]
   [rules-selector tr "new" false nil]
   [:div.flex.justify-end
    [ui-action-buttons/save-cancel-buttons
     (util/mmap {:on-cancel      (fn []
                                   (>evt [::set-create-open false])
                                   (>evt [::set-visit-result nil]))
                 :on-save        #(>evt [::create-visit-result authority-group-id])
                 :save-disabled? (<sub [::invalid-visit-result-data?])}
                tr)]]])

(defn- expanded-visit-result [tr editing-this? {id :db/id :as visit-result}]
  (let [editing-this? (editing-this? visit-result)
        rules         (when (not editing-this?) (:inspection-visit-result/rules visit-result))
        no-changes?   (<sub [::no-edit-changes? id])
        invalid-data? (<sub [::invalid-visit-result-data?])]
    [:div
     [rules-selector tr id (not editing-this?) rules]
     (when editing-this?
       [:div.pt-2.flex.justify-end
        [ui-action-buttons/save-cancel-buttons
         {:on-cancel      #(>evt [::set-visit-result nil])
          :on-save        #(>evt [::edit-visit-result])
          :save-disabled? (or no-changes? invalid-data?)
          :size           :small
          :tr             tr}]])]))

(defn- table-actions [tr visit-result]
  (let [in-use? (<sub [::visit-result-in-use visit-result])]
    [(when (not in-use?) {:title    (tr [:edit])
                          :icon     i/pen
                          :on-click #(>evt [::set-visit-result visit-result])})
     {:title           (tr [:delete])
      :icon            i/trash
      :on-click        #(>evt [::delete-visit-result visit-result])
      :confirm?        true
      :confirm-content (tr [:inspection-visit-result/delete-confirm])}]))

(defn- visit-result-table [{:keys [tr authority-group-id]}]
  (let [visit-results (<sub [::ias/visit-result-entities authority-group-id])
        editing?      (<sub [::editing-visit-result?])
        edited-id     (<sub [::get-visit-result-data :db/id])
        editing-this? (fn [{id :db/id}] (= id edited-id))
        expanded-rows (<sub [::expanded-rows])
        creating-new? (<sub [::creating-new-visit-result?])]
    [ui-table/modern-responsive-table
     {:cols              [[:inspection-visit-result/names
                           (fn [visit-result]
                             (if (editing-this? visit-result)
                               [name-field tr false]
                               [name-col visit-result]))]
                          [:inspection-visit-result/rules
                           (fn [{:inspection-visit-result/keys [rules]}]
                             (if (empty? rules) (tr [:no]) (tr [:yes])))]
                          [:inspection-visit-result/status-type
                           (fn [visit-result]
                             (if (editing-this? visit-result)
                               [status-type-selector tr false]
                               [status-type-col tr visit-result]))]]
      :expanded-func     (partial expanded-visit-result tr editing-this?)
      :expanded-row-keys expanded-rows
      :on-expand         #(>evt [::toggle-row-expansion %])
      :tr                tr
      :items             visit-results
      :make-actions      (when (and (not editing?)
                                    (not creating-new?))
                           (partial table-actions tr))}]))

(defn quick-pass-default-selector [{:keys [tr authority-group-id]}]
  (let [auth-group (<sub [::gas/group])]
    (when (<sub [::gas/has-feature? :group.feature/inspection-quick-pass])
      (let [lang (<sub [:language])]
        [:div.max-w-xs
         [ui-select/select {:id           "quick-pass-default-select"
                            :class-name   "full-width-select"
                            :label-text   (tr [:inspection-visit-result/quick-pass-default])
                            :allow-empty? false
                            :empty-text   (tr [:none])
                            :values       (<sub [::ias/quick-pass-applicable-visit-results])
                            :val->str     #(inspection-model/visit-result-name % lang)
                            :value        (:group/visit-result-quick-pass auth-group)
                            :on-change    #(>evt [::set-default-quick-pass-result authority-group-id %])}]]))))

(defn view [{:keys [tr] :as ctx}]
  (let [creating-new? (<sub [::creating-new-visit-result?])
        editing?      (<sub [::editing-visit-result?])]
    [acommon/section
     {}
     [:div.flex.flex-col.gap-y-4
      [ui-i18n/edit-language-selector {:allowed-locales-sub ::gas/allowed-locales}]
      [visit-result-table ctx]
      (if creating-new?
        [visit-result-editor ctx]
        (cc/button {:kind             :primary
                    :size             :small
                    :icon             i/plus
                    :icon-description ""
                    :on-click         #(>evt [::set-create-open true])
                    :disabled         editing?}
                   (tr [:inspection-visit-result/add-new])))
      [quick-pass-default-selector ctx]]]))
